import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { ArrowCounterClockwiseIcon } from '@phosphor-icons/react';
import { useUndoAPInvoiceLineItemSplit } from '@/api/rpc/hooks/accounts-payable/useSplitAPInvoiceLineItem';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/context/ToastContext';
import { formatCurrency } from '@/utils/formatters';

/**
 * Subtotal-row action that puts every part of a split line back into the
 * original line, after a confirmation (the parts' matches are removed).
 */
export function UndoSplitButton({
  invoiceId,
  lineItemId,
  partCount,
  originalTotalPrice,
  currency,
}: {
  invoiceId: string;
  /** Any part of the split. */
  lineItemId: string;
  partCount: number;
  originalTotalPrice: string;
  currency: string;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const undoSplit = useUndoAPInvoiceLineItemSplit();
  const label = gt('Undo split', {
    context: 'AP invoice line items: put a split line back together',
  });
  const amount = formatCurrency(Number(originalTotalPrice), { currency });

  function handleUndo() {
    undoSplit.mutate(
      { apInvoiceId: invoiceId, lineItemId },
      {
        onSuccess: () => {
          showToast({
            message: gt('Split undone', {
              context: 'toast after undoing the split of an AP invoice line',
            }),
            color: 'success',
          });
          setOpen(false);
        },
        onError: (error) => {
          showToast({ message: error.message, color: 'error' });
        },
      },
    );
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            aria-label={label}
            onClick={() => setOpen(true)}
          >
            <ArrowCounterClockwiseIcon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="undo split confirmation title">Undo this split?</T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T context="undo split confirmation: what happens">
                The <Var>{partCount}</Var> parts become one line of{' '}
                <Var>{amount}</Var> again, as it was on the invoice. Matches on
                the parts are removed. The invoice total does not change.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={undoSplit.isPending}>
              <T context="undo split confirmation: cancel button">Cancel</T>
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={undoSplit.isPending}
              onClick={(event) => {
                event.preventDefault();
                handleUndo();
              }}
            >
              <T context="undo split confirmation: confirm button">
                Undo Split
              </T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
